import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

function OtpModal({ allocationId, employeeEmail, token, onClose, onSuccess }) {
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async () => {
    if (!otpValue.trim()) { setOtpError("Please enter the OTP."); return; }
    setVerifying(true);
    setOtpError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-allocations/${allocationId}/verify-otp`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ otp: otpValue.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setOtpError(data.error || "Invalid OTP."); return; }
      toast.success("Acknowledgment confirmed! Asset handover complete.");
      onSuccess();
    } catch { setOtpError("Verification failed. Please try again."); }
    finally { setVerifying(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-allocations/${allocationId}/resend-otp`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Failed to resend OTP."); return; }
      toast.info("OTP resent to employee's email.");
      setOtpValue("");
      setOtpError("");
    } catch { toast.error("Failed to resend OTP."); }
    finally { setResending(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, backdropFilter: "blur(2px)" }} />
      <div role="dialog" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1060, width: "min(420px,90vw)", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#1e3a5f,#2d5a9e)" }} />
        <div style={{ padding: "28px 28px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔐</div>
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#111827" }}>Acknowledge Asset Handover</h5>
              <p style={{ margin: "3px 0 0", fontSize: 13, color: "#6b7280" }}>
                OTP sent to <strong>{employeeEmail || "employee's email"}</strong>
              </p>
            </div>
          </div>
          <div style={{ background: "#f0f4fa", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#374151", marginBottom: 20, lineHeight: 1.6 }}>
            Ask the employee to check their email and provide the 6-digit OTP to confirm receipt of the asset.
          </div>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
            Enter OTP <span style={{ color: "#dc3545" }}>*</span>
          </label>
          <input type="text" inputMode="numeric" maxLength={6} value={otpValue}
            onChange={e => { setOtpValue(e.target.value.replace(/\D/g, "")); setOtpError(""); }}
            onKeyDown={e => e.key === "Enter" && handleVerify()}
            placeholder="6-digit OTP" autoFocus
            style={{ width: "100%", padding: "10px 14px", fontSize: 22, fontWeight: 700, letterSpacing: 8, textAlign: "center", border: `2px solid ${otpError ? "#dc3545" : "#d1d5db"}`, borderRadius: 8, outline: "none", boxSizing: "border-box" }} />
          {otpError && <p style={{ margin: "6px 0 0", fontSize: 13, color: "#dc3545" }}>{otpError}</p>}
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button onClick={handleVerify} disabled={verifying || otpValue.length !== 6}
              style={{ flex: 1, padding: "10px", background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: otpValue.length !== 6 ? "not-allowed" : "pointer", opacity: otpValue.length !== 6 ? 0.6 : 1 }}>
              {verifying ? "Verifying..." : "Confirm Acknowledgment"}
            </button>
            <button onClick={onClose} style={{ padding: "10px 16px", background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              Skip
            </button>
          </div>
          <p style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: "#6b7280" }}>
            Didn't receive?{" "}
            <button onClick={handleResend} disabled={resending}
              style={{ background: "none", border: "none", color: "#2d5a9e", fontWeight: 600, cursor: "pointer", padding: 0, fontSize: 13 }}>
              {resending ? "Sending..." : "Resend OTP"}
            </button>
          </p>
        </div>
      </div>
    </>
  );
}

export default function AllAllocations() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [otpModal, setOtpModal] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  // useEffect(() => {
  //   const roles = JSON.parse(localStorage.getItem("roles") || "[]");
  //   if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
  //   if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  // }, [navigate, token]);

  const fetchAllocations = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-allocations?page=0&size=200`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const list = Array.isArray(data) ? data : Array.isArray(data.content) ? data.content : [];
      setAllocations(list);
    } catch { setAllocations([]); toast.error("Failed to load allocations."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchAllocations(); }, [token]);

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (typeFilter) params.set("type", typeFilter);
      const res = await fetch(`${API_BASE_URL}/api/asset-allocations/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = (search || typeFilter) ? `Allocations_Filtered_${date}.xlsx` : `Allocations_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const active = useMemo(() => allocations.filter(a => a.status === "ACTIVE"), [allocations]);
  const returned = useMemo(() => allocations.filter(a => a.status === "RETURNED"), [allocations]);

  const filterList = (list) => {
    let result = list;
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(a =>
        (a.assetCode || "").toLowerCase().includes(q) ||
        (a.assetName || "").toLowerCase().includes(q) ||
        (a.employeeName || "").toLowerCase().includes(q) ||
        (a.allocationType || "").toLowerCase().includes(q)
      );
    }
    if (typeFilter) {
      result = result.filter(a => (a.allocationType || "") === typeFilter);
    }
    return result;
  };

  const filteredActive = filterList(active);
  const filteredReturned = filterList(returned);

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-person-check me-2"></i> All Allocations</h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>Track current and past asset assignments across all employees.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate("/newallocation")}>
              <i className="bi bi-plus-circle me-1"></i> New Allocation
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allassets")}>
              <i className="bi bi-box-seam me-1"></i> Asset List
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadReport} disabled={downloading}>
              <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Active Allocations", value: active.length, icon: "bi-person-check", color: "#198754" },
            { label: "Acknowledged", value: active.filter(a => a.acknowledged).length, icon: "bi-check-circle", color: "#0d6efd" },
            { label: "Pending Acknowledgement", value: active.filter(a => !a.acknowledged).length, icon: "bi-hourglass-split", color: "#fd7e14" },
            { label: "Returned", value: returned.length, icon: "bi-arrow-return-left", color: "#6b7280" },
          ].map(item => (
            <div className="col-md-3 col-6" key={item.label}>
              <div className="bg-white border rounded p-2 d-flex align-items-center gap-2">
                <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 20 }}></i>
                <div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 18, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(p => !p)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>
        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by asset code, name, employee..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="Permanent">Permanent</option>
                <option value="Temporary">Temporary</option>
                <option value="Project-based">Project-based</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(""); setTypeFilter(""); }}>Clear</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading allocations...</div>
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0 fw-semibold">
                Current Allocations
                <span className="badge bg-success ms-2">{filteredActive.length}</span>
              </h6>
              {returned.length > 0 && (
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowHistory(h => !h)}>
                  <i className="bi bi-clock-history me-1"></i>
                  {showHistory ? "Hide History" : `Show History (${returned.length})`}
                </button>
              )}
            </div>

            {filteredActive.length === 0 ? (
              <p className="text-muted">No active allocations found.</p>
            ) : (
              <div style={{ overflowX: "auto" }} className="mb-4">
                <table className="table table-sm align-middle text-start">
                  <thead className="table-light">
                    <tr>
                      <th>View | Edit</th>
                      <th>Asset Code</th><th>Asset Name</th><th>Employee</th>
                      <th>Type</th><th>Allocated On</th><th>Expected Return Date</th>
                      <th>Acknowledged</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActive.map(a => (
                      <tr key={a.id}>


                        <td>
                          <Link to={`/viewallocation/${a.id}`} className="me-1 text-primary" style={{ fontSize: 16 }}>View</Link>|
                          <Link to={`/editallocation/${a.id}`} className="ms-1 text-primary" style={{ fontSize: 16 }}>Edit</Link>
                        </td>

                        <td><span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontFamily: "monospace" }}>{a.assetCode}</span></td>
                        <td>{a.assetName}</td>
                        <td>{a.employeeName}</td>
                        <td>{a.allocationType}</td>
                        <td>{a.allocationDate}</td>
                        <td>{a.expectedReturnDate || "—"}</td>
                        <td>
                          {a.acknowledged
                            ? <span className="badge bg-success">✓ Confirmed</span>
                            : <span className="badge bg-warning text-dark">Pending</span>}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {!a.acknowledged && (
                              <button className="btn btn-sm btn-outline-secondary"
                                onClick={() => setOtpModal({ allocationId: a.id, employeeEmail: a.employeeEmail || "" })}>
                                OTP
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showHistory && returned.length > 0 && (
              <div className="mt-2">
                <h6 className="mb-2 text-muted">
                  <i className="bi bi-clock-history me-1"></i> Allocation History
                  <span className="badge bg-secondary ms-2">{filteredReturned.length}</span>
                </h6>
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-sm align-middle text-start text-muted">
                    <thead style={{ background: "#f8f9fa" }}>
                      <tr>
                        <th>Asset Code</th><th>Asset Name</th><th>Employee</th>
                        <th>Type</th><th>Allocated On</th><th>Return Date</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReturned.map(a => (
                        <tr key={a.id} style={{ opacity: 0.75 }}>
                          <td><span className="badge bg-secondary">{a.assetCode}</span></td>
                          <td>{a.assetName}</td>
                          <td>{a.employeeName}</td>
                          <td>{a.allocationType}</td>
                          <td>{a.allocationDate}</td>
                          <td>{a.returnDate || "—"}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/viewallocation/${a.id}`)}>
                                <i className="bi bi-eye me-1"></i>View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {otpModal && (
        <OtpModal
          allocationId={otpModal.allocationId}
          employeeEmail={otpModal.employeeEmail}
          token={token}
          onClose={() => setOtpModal(null)}
          onSuccess={() => { setOtpModal(null); fetchAllocations(); }}
        />
      )}
    </>
  );
}
